import React, { createContext, useContext, useMemo } from 'react';
import merge from 'lodash.merge';
import { usePathname } from 'next/navigation';

import sendSentryMessage from '@/lib/sentry/helpers/sendSentryMessage';

import SortAndFilterConfig from '../../../../modules/sortAndFilter/config/SortAndFilterConfig';
import jsonToObject from '../helpers/jsonToObject';
import localSortAndFilterConfig from '../../../../modules/sortAndFilter/config/localSortAndFilterConfig.json';
import { GlobalSortAndFilterConfig } from '../../../../lib/sanity/queries/globalSortAndFilterConfig.query';

type UsePathnameReturnType = ReturnType<typeof usePathname>;

interface Props {
    children: React.ReactNode;
    // Type is same as the global as both are from sanity.
    pageConfig?: GlobalSortAndFilterConfig['sortAndFilterConfig'];
    globalConfig?: GlobalSortAndFilterConfig['sortAndFilterConfig'];
}

const SortAndFilterConfigContext = createContext<SortAndFilterConfig>({} as SortAndFilterConfig);

/**
 * If we are on the search-results page, manually add a sort option.
 */
const applySearchPageConfigChanges = (config: SortAndFilterConfig, pathName: UsePathnameReturnType) => {
    if (pathName === '/search-results') {
        /* eslint-disable no-param-reassign */
        config.sorting.default = 'relevance';
        config.sorting.options.relevance = {
            enabled: true,
            order: 0
        };
        /* eslint-enable no-param-reassign */
    }

    return config;
}

/**
 * Takes a page level Sort and Filter config and merges it over the top of the global config.
 */
const SortAndFilterConfigProvider = ({ pageConfig, globalConfig, children }: Props) => {
    const pathName = usePathname();

    const config: SortAndFilterConfig = useMemo(() => {
        // NOTE: localSortAndFilterConfig is a last resort case. If no global config is available, we will
        // use this local backup config.
        let globalConfigObject = localSortAndFilterConfig as SortAndFilterConfig;

        // If the global config is given, parse it from a string to JSON. If it fails, revert back to the
        // local config.
        if (globalConfig) {
            globalConfigObject = jsonToObject<SortAndFilterConfig, null>(globalConfig, null, (error) => {
                sendSentryMessage('Unable to parse page Sort and Filter config from CMS.', (scope) => {
                    scope.setExtra('Json config', pageConfig);
                    scope.setExtra('Parsing error message', error.message);
                });
            }) || globalConfigObject;
        }

        // If no page config has been set.
        if (!pageConfig) {
            return applySearchPageConfigChanges(globalConfigObject, pathName);
        }

        const pageConfigObject = jsonToObject<SortAndFilterConfig, object>(pageConfig, {}, (error) => {
            sendSentryMessage('Unable to parse page Sort and Filter config from CMS.', (scope) => {
                scope.setExtra('Json config', pageConfig);
                scope.setExtra('Parsing error message', error.message);
            });
        });

        return applySearchPageConfigChanges(merge(globalConfigObject, pageConfigObject), pathName);
    }, [pageConfig, globalConfig, pathName]);

    return (
        <SortAndFilterConfigContext.Provider value={config}>{children}</SortAndFilterConfigContext.Provider>
    );
};

export const useSortAndFilterConfigContext = () => {
    const config = useContext(SortAndFilterConfigContext);

    if (config === undefined) {
        throw new Error('useSortAndFilterConfigContext must be used within a SortAndFilterConfigProvider.');
    }

    return config;
};

export default SortAndFilterConfigProvider;
